/**
 * PremiumRequiredModal - Visar när en funktion kräver Premium
 * Återanvändbar komponent för att visa Premium-krav
 */

import { useEffect, useMemo, useRef } from 'react';
import { Crown, Sparkles, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import ModalBackdrop from '@/components/ModalBackdrop';

interface PremiumRequiredModalProps {
  open: boolean;
  onClose: () => void;
  feature?: string;
}

export default function PremiumRequiredModal({ 
  open, 
  onClose,
  feature = 'Denna funktion'
}: PremiumRequiredModalProps) {
  const modalRef = useRef<HTMLDivElement | null>(null);

  const displayFeature = useMemo(() => {
    if (feature === 'Importera/Exportera beredskapsdata') {
      return 'Importera beredskapsdata';
    }

    return feature;
  }, [feature]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const preventScroll = (event: WheelEvent | TouchEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        event.preventDefault();
        return;
      }

      if (modalRef.current?.contains(target)) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
    };

    document.addEventListener('wheel', preventScroll, { passive: false, capture: true });
    document.addEventListener('touchmove', preventScroll, { passive: false, capture: true });

    return () => {
      document.removeEventListener('wheel', preventScroll, { capture: true });
      document.removeEventListener('touchmove', preventScroll, { capture: true });
    };
  }, [open]);

  if (!open) return null;

  return (
    <ModalBackdrop onClick={onClose} className="z-[9999] overflow-hidden overscroll-none p-4">
      <div
        ref={modalRef}
        className="relative w-full max-w-md bg-card rounded-xl border border-border shadow-lg"
        onClick={(event) => event.stopPropagation()}
        onWheel={(event) => event.preventDefault()}
        onTouchMove={(event) => event.preventDefault()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-lg hover:bg-muted transition-colors"
          aria-label="Stäng"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Content */}
        <div className="p-6">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 p-4">
              <Crown className="h-8 w-8 text-amber-500 dark:text-amber-400" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-center mb-2">
            Premium krävs
          </h2>

          {/* Description */}
          <p className="text-center text-muted-foreground mb-6">
            {displayFeature} kräver Premium för att användas.
          </p>

          {/* Features list */}
          <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Med Premium får du bland annat:
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Beredskapsplanering för 30 och 90 dagar+</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Årshjul för återkommande beredskapsunderhåll</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Full åtkomst till Kunskapsdatabasen</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Familjeläge med delad beredskap</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Obegränsad funktionalitet</span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
            >
              Avbryt
            </button>
            <Link
              to="/app/settings#get-premium"
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg btn-primary-custom font-medium flex items-center justify-center gap-2"
            >
              <Crown className="h-4 w-4" />
              Skaffa Premium
            </Link>
          </div>

          {/* Price info */}
          <p className="text-center text-xs text-muted-foreground mt-4">
            Premium kostar 69 kr/mån
          </p>
        </div>
      </div>
    </ModalBackdrop>
  );
}
